import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import { RecipientDto, RecipientEditDto } from 'src/store/recipients/recipient';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum RecipientTypes {
  FetchAll = 'recipients/FetchAll',
  Add = 'recipients/Add',
  Update = 'recipients/Update',
  Delete = 'recipients/Delete',
  Failure = 'recipients/Failure'
}

export type RecipientAction =
  | { type: RecipientTypes.FetchAll; recipients: RecipientDto[] }
  | { type: RecipientTypes.Add; recipient: RecipientDto }
  | { type: RecipientTypes.Update; recipient: RecipientDto }
  | { type: RecipientTypes.Delete; recipient: RecipientDto }
  | { type: RecipientTypes.Failure; errors: any };

export const actionCreators = {
  fetchRecipients: (recipients: RecipientDto[]): RecipientAction => ({
    type: RecipientTypes.FetchAll,
    recipients
  }),
  addRecipient: (recipient: RecipientDto): RecipientAction => ({
    type: RecipientTypes.Add,
    recipient
  }),
  updateRecipient: (recipient: RecipientDto): RecipientAction => ({
    type: RecipientTypes.Update,
    recipient
  }),
  deleteRecipient: (recipient: RecipientDto): RecipientAction => ({
    type: RecipientTypes.Delete,
    recipient
  }),
  recipientRequestFailure: (errors: any): RecipientAction => ({
    type: RecipientTypes.Failure,
    errors
  })
};

export const fetchRecipients = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(RecipientTypes.FetchAll));
    const { ok, data, error } = await getRequest<RecipientDto[]>('/recipients');
    dispatch(requestEnd(RecipientTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchRecipients(data))
      : dispatch(actionCreators.recipientRequestFailure(error));
  };
};

export const createRecipient = (recipient: RecipientEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>(
      '/recipients',
      recipient
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('recipient.created')));
      return dispatch(actionCreators.addRecipient({ ...recipient, id: data }));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.recipientRequestFailure(error));
  };
};

export const editRecipient = (id: number, recipient: RecipientEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/recipients/${id}`, recipient);

    if (ok) {
      dispatch(alertSuccess(i18n.t('recipient.updated')));
      return dispatch(
        actionCreators.updateRecipient(recipient as RecipientDto)
      );
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.recipientRequestFailure(error));
  };
};

export const deleteRecipient = (id: number, recipient: RecipientDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/recipients/${id}`, recipient);

    if (ok) {
      dispatch(alertSuccess(i18n.t('recipient.deleted')));
      return dispatch(actionCreators.deleteRecipient(recipient));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.recipientRequestFailure(error));
  };
};
